import './App.css';
import Header from './components/Header/Header';
import PropertyCard from './components/PropertyCard/PropertyCard';
import PropertyDetail from './components/PropertyDetail/PropertyDetail';
import Map from './components/Map/Map';
import Footer from './components/Footer/Footer';
import ContactModal from './components/Header/ContactModal';
import React, { useMemo, useState, useEffect } from 'react';
import chevronDown from './assets/images/chevron-down.svg';
import { ChevronUp } from 'lucide-react';

import { motion, AnimatePresence } from 'framer-motion';

function App() {
  const [properties, setProperties] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('listings'); // 'listings' or 'property'
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openMobileFiltre1, setOpenMobileFiltre1] = useState(false);
  const [openMobileRegion, setOpenMobileRegion] = useState(false);
  const [mobileRegionStep, setMobileRegionStep] = useState('city'); // 'city' or 'district'
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.split-button')) {
        setOpenMobileFiltre1(false);
        setOpenMobileRegion(false);
        setMobileRegionStep('city');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadData = () => {
      setLoading(true);
      setError(null);
      try {
        fetch('/listings.json')
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then(data => {
            const formattedProperties = data.map(p => {
              return {
                ...p,
                listingType: p.operationName || (p.price && parseInt(p.price.replace(/\D/g, '')) < 100000 ? 'Kiralık' : 'Satılık'),
                city: p.cityName || '',
                district: p.townName || '',
                coords: {
                  lat: p.latitude,
                  lng: p.longitude
                },
                image: p.mainImage,
                allImages: p.images || [p.mainImage],
                details: {
                  odaSayisi: p.headerAttributes?.['Oda Sayısı'],
                  binaYasi: p.headerAttributes?.['Bina Yapım Yılı'],
                  bulunduguKat: p.headerAttributes?.['Bulunduğu Kat'],
                  katSayisi: p.headerAttributes?.['Kat Sayısı'],
                  isitma: p.headerAttributes?.['Isıtma'],
                  banyoSayisi: p.headerAttributes?.['Banyo Sayısı'],
                  balkon: p.headerAttributes?.['Balkon'],
                  esyali: p.headerAttributes?.['Eşyalı'],
                  kullanimDurumu: p.headerAttributes?.['Kullanım Durumu'],
                  metreKare: p.headerAttributes?.['m²'] || p.headerAttributes?.['m2 (Brüt)'],
                  tapuDurumu: p.headerAttributes?.['Tapu Durumu'],
                  arsaDurumu: p.headerAttributes?.['Arsa Durumu'],
                  gabari: p.headerAttributes?.['Gabari'],
                  adaNo: p.headerAttributes?.['Ada'],
                  parselNo: p.headerAttributes?.['Parsel']
                },
                agent: {
                  name: p.propertyDetailEmployee?.name,
                  photo: p.propertyDetailEmployee?.photo,
                  phone: p.propertyDetailEmployee?.phoneNumbers?.[0],
                  office: p.propertyDetailEmployee?.officeId || 'RE/MAX West'
                }
              };
            });
            setProperties(formattedProperties);
            setTotalCount(data.length);
          })
          .catch(e => {
            setError(e.message);
            console.error("Failed to fetch listings:", e);
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (e) {
        setError(e.message);
        console.error("Failed to load listings:", e);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const lastVowel = (word) => {
    const vowels = ['a', 'e', 'ı', 'i', 'o', 'ö', 'u', 'ü', 'A', 'E', 'I', 'İ', 'O', 'Ö', 'U', 'Ü'];
    for (let i = word.length - 1; i >= 0; i--) {
      if (vowels.includes(word[i])) return word[i].toLowerCase();
    }
    return 'a';
  };

  const locativeKiSuffix = (city) => {
    if (!city) return '';
    const voiceless = new Set(['ç', 'f', 'h', 'k', 'p', 's', 'ş', 't', 'Ç', 'F', 'H', 'K', 'P', 'S', 'Ş', 'T']);
    const lastChar = city[city.length - 1];
    const useT = voiceless.has(lastChar);
    const lv = lastVowel(city);
    const isBack = ['a', 'ı', 'o', 'u'].includes(lv);
    const dOrT = useT ? 't' : 'd';
    const aOrE = isBack ? 'a' : 'e';
    return `${dOrT}${aOrE}`;
  };

  const cityData = useMemo(() => {
    const cities = {};
    properties.forEach(p => {
        if (!cities[p.city]) {
            cities[p.city] = new Set();
        }
        if (p.district) {
            cities[p.city].add(p.district);
        }
    });
    Object.keys(cities).forEach(city => {
        cities[city] = Array.from(cities[city]);
    });
    return cities;
  }, [properties]);

  const [openCities, setOpenCities] = useState(false);
  const [openDistricts, setOpenDistricts] = useState(false);
  const [selectedCity, setSelectedCity] = useState('Tüm iller');
  const [selectedDistrict, setSelectedDistrict] = useState('Tüm ilçeler');
  const [filterType, setFilterType] = useState('Tümü');
  const titleText = selectedCity === 'Tüm iller' ? 'Tüm Konaklamalar' : `${selectedCity}'${locativeKiSuffix(selectedCity)}ki Konaklamalar`;

  const toggleCities = () => setOpenCities((v) => !v);
  const toggleDistricts = () => setOpenDistricts((v) => !v);
  const selectCity = (c) => {
    setSelectedCity(c);
    setOpenCities(false);
    setSelectedDistrict('Tüm ilçeler');
    setOpenDistricts(false);
  };
  const selectDistrict = (d) => {
    setSelectedDistrict(d);
    setOpenDistricts(false);
    setOpenMobileRegion(false);
    setMobileRegionStep('city');
  };

  const handleMobileCitySelect = (c) => {
    selectCity(c);
    if (c === 'Tüm iller') {
      setOpenMobileRegion(false);
    } else {
      setMobileRegionStep('district');
    }
  };

  const handleMobileRegionToggle = () => {
    setOpenMobileRegion(!openMobileRegion);
    if (!openMobileRegion) {
      setMobileRegionStep('city');
    }
  };

  const displayedProperties = useMemo(() => {
    let filtered = properties;
    if (filterType !== 'Tümü') {
        filtered = filtered.filter((p) => {
            const type = (p.listingType || '').toLowerCase();
            const target = filterType.toLowerCase();
            if (target === 'kiralık') {
                return type === 'kiralık' || type === 'devren kiralık';
            }
            return type === target;
        });
    }
    if (selectedCity !== 'Tüm iller') {
        filtered = filtered.filter(p => p.city === selectedCity);
        if (selectedDistrict !== 'Tüm ilçeler') {
            filtered = filtered.filter(p => p.district === selectedDistrict);
        }
    }
    return filtered;
  }, [properties, filterType, selectedCity, selectedDistrict]);

  const [selectedIdx, setSelectedIdx] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  useEffect(() => {
    if (filterType === 'Tümü') {
      setSelectedIdx(null);
    }
  }, [filterType]);

  const focusCard = React.useCallback((idx) => {
    if (currentView !== 'listings') return;
    setSelectedIdx(idx);
    const el = document.getElementById(`property-card-${idx}`);
    if (el) {
      try {
        el.scrollIntoView({ 
          behavior: 'smooth', 
          block: window.innerWidth <= 1024 ? 'nearest' : 'center',
          inline: 'center' 
        });
      } catch (e) {
        const rect = el.getBoundingClientRect();
        const y = rect.top + window.pageYOffset - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  }, [currentView]);

  const navigateToDetail = (property) => {
    setSelectedProperty(property);
    setCurrentView('property');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backToListings = () => {
    setCurrentView('listings');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const mapMarkers = useMemo(() => {
    return displayedProperties
      .filter(p => p.coords && (p.coords.lat !== 0 || p.coords.lng !== 0))
      .map((p) => ({
        lat: p.coords?.lat,
        lng: p.coords?.lng,
        title: p.title,
        idx: properties.indexOf(p),
      }));
  }, [displayedProperties, properties]);

  if (loading) {
    return <div className="loading-container">Veriler yükleniyor, lütfen bekleyin...</div>;
  }

  if (error) {
    return <div className="error-container">Veri yüklenirken bir hata oluştu: {error}</div>;
  }

  return (
    <div className="App">
      <Header 
        totalCount={totalCount} 
        onLogoClick={backToListings} 
        onContactClick={() => setShowContactModal(true)} 
      />
      
      <ContactModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
      
      {currentView === 'property' && selectedProperty ? (
        <PropertyDetail property={selectedProperty} onBack={backToListings} />
      ) : (
        <main className="main-content">
          <div className="mobile-filters-container">
            <div className="mobile-split-buttons">
              {/* Filtre 1: Satılık/Kiralık */}
              <div className={`split-button ${openMobileFiltre1 ? 'open' : ''}`}>
                <div className="split-button-main" onClick={() => setOpenMobileFiltre1(!openMobileFiltre1)}>
                  <span className="split-button-icon">✨</span>
                  <span className="split-button-text">{filterType === 'Tümü' ? 'İlan Türü' : filterType}</span>
                </div>
                <div className="split-button-arrow" onClick={() => setOpenMobileFiltre1(!openMobileFiltre1)}>
                  <img src={chevronDown} alt="dropdown" />
                </div>
                {openMobileFiltre1 && (
                  <div className="mobile-dropdown">
                    <div className={`mobile-dropdown-item ${filterType === 'Tümü' ? 'active' : ''}`} onClick={() => { setFilterType('Tümü'); setOpenMobileFiltre1(false); }}>Tümü</div>
                    <div className={`mobile-dropdown-item ${filterType === 'Satılık' ? 'active' : ''}`} onClick={() => { setFilterType('Satılık'); setOpenMobileFiltre1(false); }}>Satılık</div>
                    <div className={`mobile-dropdown-item ${filterType === 'Kiralık' ? 'active' : ''}`} onClick={() => { setFilterType('Kiralık'); setOpenMobileFiltre1(false); }}>Kiralık</div>
                  </div>
                )}
              </div>

              {/* Bölge: İl/İlçe */}
              <div className={`split-button ${openMobileRegion ? 'open' : ''}`}>
                <div className="split-button-main" onClick={handleMobileRegionToggle}>
                  <span className="split-button-icon">📍</span>
                  <span className="split-button-text">
                    {selectedDistrict !== 'Tüm ilçeler' ? selectedDistrict : (selectedCity !== 'Tüm iller' ? selectedCity : 'Bölge')}
                  </span>
                </div>
                <div className="split-button-arrow" onClick={handleMobileRegionToggle}>
                  <img src={chevronDown} alt="dropdown" />
                </div>
                {openMobileRegion && (
                  <div className="mobile-dropdown">
                    {mobileRegionStep === 'city' ? (
                      <>
                        <div className="mobile-dropdown-header">İl Seçin</div>
                        <div className={`mobile-dropdown-item ${selectedCity === 'Tüm iller' ? 'active' : ''}`} onClick={() => handleMobileCitySelect('Tüm iller')}>Tüm iller</div>
                        {Object.keys(cityData).sort().map(city => (
                          <div key={city} className={`mobile-dropdown-item ${selectedCity === city ? 'active' : ''}`} onClick={() => handleMobileCitySelect(city)}>{city}</div>
                        ))}
                      </>
                    ) : (
                      <>
                        <div className="mobile-dropdown-header">
                          <button className="back-btn" onClick={() => setMobileRegionStep('city')}>←</button>
                          {selectedCity} - İlçe Seçin
                        </div>
                        <div className={`mobile-dropdown-item ${selectedDistrict === 'Tüm ilçeler' ? 'active' : ''}`} onClick={() => selectDistrict('Tüm ilçeler')}>Tüm ilçeler</div>
                        {(cityData[selectedCity] || []).sort().map(district => (
                          <div key={district} className={`mobile-dropdown-item ${selectedDistrict === district ? 'active' : ''}`} onClick={() => selectDistrict(district)}>{district}</div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="listings-container">
            <div className="listings-header desktop-only">
              <div className="listings-header-left">
                <div className="listings-picker-group">
                  <div
                    className="listings-picker"
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={openCities}
                    onClick={toggleCities}
                  >
                    <span>{selectedCity}</span>
                    <img src={chevronDown} alt="açılır ok" />
                    {openCities && (
                      <div className="picker-dropdown" role="listbox" aria-label="İller" onClick={(e) => e.stopPropagation()}>
                        <div
                          role="option"
                          aria-selected={selectedCity === 'Tüm iller'}
                          className="picker-item"
                          onClick={() => selectCity('Tüm iller')}
                        >
                          Tüm iller
                        </div>
                        {Object.keys(cityData).sort().map((c) => (
                          <div
                            key={c}
                            role="option"
                            aria-selected={c === selectedCity}
                            className="picker-item"
                            onClick={() => selectCity(c)}
                          >
                            {c}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div
                    className="listings-picker"
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={openDistricts}
                    onClick={toggleDistricts}
                    disabled={selectedCity === 'Tüm iller'}
                  >
                    <span>{selectedDistrict}</span>
                    <img src={chevronDown} alt="açılır ok" />
                    {openDistricts && (
                      <div className="picker-dropdown" role="listbox" aria-label="İlçeler" onClick={(e) => e.stopPropagation()}>
                        <div
                          role="option"
                          aria-selected={selectedDistrict === 'Tüm ilçeler'}
                          className="picker-item"
                          onClick={() => selectDistrict('Tüm ilçeler')}
                        >
                          Tüm ilçeler
                        </div>
                         {(cityData[selectedCity] || []).sort().map((d) => (
                          <div
                            key={d}
                            role="option"
                            aria-selected={d === selectedDistrict}
                            className="picker-item"
                            onClick={() => selectDistrict(d)}
                          >
                            {d}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <h1 className="listings-title">{titleText}</h1>
                <div className="listings-header-right">
                  <button className={`filter-btn ${filterType === 'Tümü' ? 'active' : ''}`} onClick={() => { setFilterType('Tümü'); setSelectedIdx(null); }}>
                    Tümü
                  </button>
                  <button className={`filter-btn ${filterType === 'Satılık' ? 'active' : ''}`} onClick={() => { setFilterType('Satılık'); setSelectedIdx(null); }}>
                    Satılık
                  </button>
                  <button className={`filter-btn ${filterType === 'Kiralık' ? 'active' : ''}`} onClick={() => { setFilterType('Kiralık'); setSelectedIdx(null); }}>
                    Kiralık
                  </button>
                </div>
              </div>
            </div>
            <motion.div layout className="property-cards-list">
              <AnimatePresence>
                {displayedProperties.map((prop, index) => {
                  const globalIndex = properties.indexOf(prop);
                  const isActive = selectedIdx === globalIndex;
                  return (
                    <PropertyCard
                      key={`${prop.title}-${globalIndex}`}
                      id={`property-card-${globalIndex}`}
                      property={prop}
                      animateIndex={index}
                      active={isActive}
                      onClick={() => focusCard(globalIndex)}
                      onViewDetails={navigateToDetail}
                      onMouseEnter={() => setHoveredIdx(globalIndex)}
                      onMouseLeave={() => setHoveredIdx(null)}
                    />
                  );
                })}
              </AnimatePresence>
            </motion.div>

            <AnimatePresence>
              {showScrollTop && (
                <div className="scroll-to-top-wrapper">
                  <motion.button
                    className="scroll-to-top"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    aria-label="Yukarı Çık"
                  >
                    <ChevronUp size={24} />
                  </motion.button>
                </div>
              )}
            </AnimatePresence>
          </div>
          <div className="map-view-container">
            <Map markers={mapMarkers} selectedIndex={selectedIdx} hoveredIndex={hoveredIdx} onMarkerClick={focusCard} />
          </div>
        </main>
      )}
      <Footer />
    </div>
  );
}

export default App;

